import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import withEventValue from "discourse/helpers/with-event-value";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import { i18n } from "discourse-i18n";
import AiIndicatorWave from "../ai-indicator-wave.gjs";

export default class SpamTestModal extends Component {
  @tracked testResult;
  @tracked isLoading = false;
  @tracked postUrl = "";
  @tracked isSpam;
  @tracked reason;
  @tracked llmName;
  @tracked decisionModelName;
  @tracked spamProbability;
  @tracked systemPrompt;
  @tracked sentMessage;
  @tracked scanHistory;

  @action
  async runTest() {
    this.isLoading = true;
    try {
      const response = await ajax(
        `/admin/plugins/discourse-ai/ai-spam/test.json`,
        {
          type: "POST",
          data: {
            post_url: this.postUrl,
            custom_instructions: this.args.model.customInstructions,
            llm_id: this.args.model.llmId ?? "",
            decision_model_id: this.args.model.decisionModelId ?? "",
          },
        }
      );

      this.isSpam = response.is_spam;
      this.testResult = response.is_spam
        ? i18n("discourse_ai.spam.test_modal.spam")
        : i18n("discourse_ai.spam.test_modal.not_spam");
      this.reason = response.reason;
      this.llmName = response.llm_name;
      this.decisionModelName = response.decision_model_name;
      this.spamProbability =
        response.spam_probability == null
          ? null
          : `${Math.round(response.spam_probability * 100)}%`;
      this.systemPrompt = response.system_prompt;
      this.sentMessage = response.sent_message;
      this.scanHistory = response.scan_history;
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.isLoading = false;
    }
  }

  <template>
    <DModal
      class="spam-test-modal"
      @bodyClass="spam-test-modal__body"
      @closeModal={{@closeModal}}
      @title={{i18n "discourse_ai.spam.test_modal.title"}}
    >
      <:body>
        <div class="control-group">
          <label>{{i18n "discourse_ai.spam.test_modal.post_url_label"}}</label>
          <input
            placeholder={{i18n
              "discourse_ai.spam.test_modal.post_url_placeholder"
            }}
            type="text"
            {{on "input" (withEventValue (fn (mut this.postUrl)))}}
          />
        </div>

        {{#if this.testResult}}
          <div class="spam-test-modal__results">
            <div
              class="spam-test-modal__verdict
                {{if this.isSpam 'is-spam' 'not-spam'}}"
            >
              {{this.testResult}}
            </div>

            {{#if this.reason}}
              <div
                class="spam-test-modal__info-box spam-test-modal__info-box--reason"
              >
                <h4 class="spam-test-modal__info-box-title">{{i18n
                    "discourse_ai.spam.test_modal.reason"
                  }}</h4>
                <div class="spam-test-modal__info-box-content">
                  <p>{{this.reason}}</p>
                </div>
              </div>
            {{/if}}

            <div class="spam-test-modal__info-grid">
              {{#if this.llmName}}
                <div
                  class="spam-test-modal__info-box spam-test-modal__info-box--llm"
                >
                  <h4 class="spam-test-modal__info-box-title">{{i18n
                      "discourse_ai.spam.test_modal.llm"
                    }}</h4>
                  <div class="spam-test-modal__info-box-content">
                    {{this.llmName}}
                  </div>
                </div>
              {{/if}}

              {{#if this.decisionModelName}}
                <div
                  class="spam-test-modal__info-box spam-test-modal__info-box--decision-model"
                >
                  <h4 class="spam-test-modal__info-box-title">{{i18n
                      "discourse_ai.spam.test_modal.decision_model"
                    }}</h4>
                  <div class="spam-test-modal__info-box-content">
                    {{this.decisionModelName}}
                  </div>
                </div>
              {{/if}}

              {{#if this.spamProbability}}
                <div
                  class="spam-test-modal__info-box spam-test-modal__info-box--probability"
                >
                  <h4 class="spam-test-modal__info-box-title">{{i18n
                      "discourse_ai.spam.test_modal.spam_probability"
                    }}</h4>
                  <div class="spam-test-modal__info-box-content">
                    {{this.spamProbability}}
                  </div>
                </div>
              {{/if}}

              {{#if this.scanHistory}}
                <div
                  class="spam-test-modal__info-box spam-test-modal__info-box--history"
                >
                  <h4 class="spam-test-modal__info-box-title">{{i18n
                      "discourse_ai.spam.test_modal.scan_history"
                    }}</h4>
                  <div class="spam-test-modal__info-box-content">
                    <pre>{{this.scanHistory}}</pre>
                  </div>
                </div>
              {{/if}}
            </div>

            {{#if this.sentMessage}}
              <div
                class="spam-test-modal__info-box spam-test-modal__info-box--message"
              >
                <h4 class="spam-test-modal__info-box-title">{{i18n
                    "discourse_ai.spam.test_modal.sent_message"
                  }}</h4>
                <div class="spam-test-modal__info-box-content">
                  <pre>{{this.sentMessage}}</pre>
                </div>
              </div>
            {{/if}}

            {{#if this.systemPrompt}}
              <div
                class="spam-test-modal__info-box spam-test-modal__info-box--prompt"
              >
                <h4 class="spam-test-modal__info-box-title">{{i18n
                    "discourse_ai.spam.test_modal.system_prompt"
                  }}</h4>
                <div class="spam-test-modal__info-box-content">
                  <pre>{{this.systemPrompt}}</pre>
                </div>
              </div>
            {{/if}}
          </div>
        {{/if}}
      </:body>

      <:footer>
        <DButton
          class="btn-primary spam-test-modal__run-button"
          @action={{this.runTest}}
          @disabled={{this.isLoading}}
          @label="discourse_ai.spam.test_modal.run"
        >
          <AiIndicatorWave @loading={{this.isLoading}} />
        </DButton>
      </:footer>
    </DModal>
  </template>
}
